import { fmtCell, isMoneyKey, moneyForms } from "./columns";

// MULTI-VALUE SEARCH — pasting a column of reference numbers out of Excel and getting
// every row that matches ANY of them.
//
// Finance works from a worksheet where each row already carries the reference it is
// chasing. Before this, the search bar took one literal substring, so pasting 24 refs
// matched the single impossible string "ref1\nref2\n…" and reported nothing — they had
// to search, match, and re-search 24 times.
//
// The delimiters are Excel's own, not a guess: the clipboard flavour Excel (and Sheets,
// and this app's own toTsv) writes is TAB between cells and CR/LF between rows. Space is
// deliberately NOT a delimiter — real references contain spaces ("INV 123", "Pol No /
// Clm No"), and splitting on space would shatter them into terms that match everything.
// A cell whose own text contains a newline or a tab is exported by Excel wrapped in
// double quotes, so quoted runs are honoured before splitting.

// One parsed search term. `raw` is kept for reporting a value back to the user exactly
// as they pasted it; `needle` is the normalised (trimmed, lower-cased) form used for
// comparison.
export interface SearchTerm {
  raw: string;
  needle: string;
}

// Split pasted text into cells on TAB / CR / LF, honouring Excel's quoting: a cell
// containing a delimiter is emitted as "...", and a literal quote inside it is doubled
// ("" -> "). Anything not inside quotes splits normally.
//
// Written as a single character scan rather than a regex split so the quote state is
// tracked correctly; a regex cannot know whether a given newline sits inside a quoted
// cell.
function splitCells(text: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"') {
        // "" inside a quoted cell is one literal quote; a lone " closes the cell.
        if (text[i + 1] === '"') { cur += '"'; i++; }
        else quoted = false;
      } else {
        cur += ch;
      }
      continue;
    }
    if (ch === '"' && cur.trim() === "") {
      // A quote only opens a cell at the START of one. Mid-cell (5" pipe) it is data.
      quoted = true;
      cur = "";
      continue;
    }
    if (ch === "\t" || ch === "\n" || ch === "\r") {
      out.push(cur);
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(cur);
  return out;
}

// Parse the search box's contents into distinct terms.
//
// Order is preserved (the user reads the report back against their own worksheet) and
// duplicates are dropped — a pasted column routinely repeats a reference, and searching
// it twice cannot find anything new but would double-count it in the coverage report.
// Blank cells (trailing newline, an empty column in a pasted block) are skipped.
export function parseTerms(text: string): SearchTerm[] {
  const out: SearchTerm[] = [];
  const seen = new Set<string>();
  for (const cell of splitCells(text)) {
    const raw = cell.trim();
    if (!raw) continue;
    const needle = raw.toLowerCase();
    if (seen.has(needle)) continue;
    seen.add(needle);
    out.push({ raw, needle });
  }
  return out;
}

// True once the box holds more than one distinct value — i.e. the multi-value affordances
// (column picker, exact toggle, coverage report) are worth showing. A single term keeps
// the plain search bar it has always been.
export function isMultiTerm(terms: SearchTerm[]): boolean {
  return terms.length > 1;
}

// The searchable text of one cell, formatted the way the grid DISPLAYS it.
//
// Mirrors textSearch.ts::haystackCell and exists for the same reason: a date reads
// dd/mm/yyyy on screen but yyyy-mm-dd in the payload, and a blank renders as an em-dash.
// Both forms of a date are searchable so a value typed as seen and a value copied from an
// export both hit. Kept as its own function rather than imported because this one returns
// the two date forms SEPARATELY — exact (whole-cell) matching must compare against each
// complete form, not against the concatenation "30/06/2025 2025-06-30", which equals
// neither and would make every exact date search fail.

// The pandas/JSON sentinels that fmtCell renders as an em-dash. They must not be
// searchable: the grid shows a blank, so matching the literal text "nan" would surface a
// row the user cannot see the value in — and in exact mode a stray "nan" cell in a pasted
// column would quietly select real rows.
//
// Case-SENSITIVE, exactly as fmtCell tests them. The fast path below would otherwise
// disagree with the fmtCell path on an oddly-cased literal ("NAN"), making one column
// searchable and another not for the same text.
const BLANK_SENTINELS = new Set(["", "nan", "NaT", "None"]);

export function cellForms(value: unknown, colKey: string): string[] {
  if (value === null || value === undefined) return [];
  const t = typeof value;
  // Returned separately, like a date's two forms, so a whole-cell paste of any one hits.
  if (t === "number" && isMoneyKey(colKey)) return moneyForms(value as number);
  if ((t === "string" || t === "number") && !colKey.toLowerCase().includes("date")) {
    const s = String(value).trim();
    return s && !BLANK_SENTINELS.has(s) ? [s.toLowerCase()] : [];
  }
  const shown = fmtCell(value, colKey);
  if (shown === "—") return [];
  const raw = String(value).trim();
  return raw && raw !== shown
    ? [shown.toLowerCase(), raw.toLowerCase()]
    : [shown.toLowerCase()];
}

// A paired grid's column keys are composite (cri::doc_date); the side prefix must be
// stripped before the date check or a paired date column formats as a plain string and
// stops matching. Same rule as textSearch.ts::visibleCellsMatch.
export function baseKey(key: string): string {
  const sep = key.indexOf("::");
  return sep >= 0 ? key.slice(sep + 2) : key;
}

// Does this cell match this term? `exact` compares the WHOLE cell (either date form),
// otherwise it is a substring test — the behaviour the single-value search has always
// had, kept as the default so a reference embedded in a narration is still found.
function cellMatches(forms: string[], needle: string, exact: boolean): boolean {
  for (const f of forms) {
    if (exact ? f === needle : f.includes(needle)) return true;
  }
  return false;
}

// "Does this cell hold ANY of the terms?", compiled once per paste. The scans below run it
// on every cell and walk the terms one by one only on the few cells it passes, to learn
// WHICH hit: a 1,000-reference paste over Dubai was ~1.3 billion includes() per pass.
// Whole-cell mode is a Set lookup; substring mode one native regex, an alternation of the
// escaped literal needles, so it agrees with cellMatches on every cell by construction.
const ANY_TERM_CACHE = new WeakMap<readonly SearchTerm[], Map<boolean, (forms: string[]) => boolean>>();

function anyTerm(terms: readonly SearchTerm[], exact: boolean): (forms: string[]) => boolean {
  let byMode = ANY_TERM_CACHE.get(terms);
  if (!byMode) { byMode = new Map(); ANY_TERM_CACHE.set(terms, byMode); }
  let test = byMode.get(exact);
  if (!test) {
    if (!terms.length) {
      test = () => false;
    } else if (exact) {
      const set = new Set(terms.map((t) => t.needle));
      test = (forms) => forms.some((f) => set.has(f));
    } else {
      const re = new RegExp(terms.map((t) => t.needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|"));
      test = (forms) => forms.some((f) => re.test(f));
    }
    byMode.set(exact, test);
  }
  return test;
}

// One column's fitness for the pasted values: how many DISTINCT terms it contains.
// This is what drives the "Search in" picker — the user pastes a column of references
// and the picker tells them which column those references actually live in, rather than
// listing every column in the frame.
export interface ColumnCoverage {
  key: string;
  label: string;
  index: number;
  hits: number;      // distinct terms found in this column
  rows: number;      // rows this column alone would surface
}

export interface MatchColumn {
  key: string;
  label: string;
  index: number;
}

// Score every candidate column against the terms, best-first.
//
// Columns matching NOTHING are dropped: the user asked not to be shown columns their
// values do not appear in, and a zero-hit column can only add noise. Ties break on the
// column's natural (curated-first) order, so the leading business columns win over a
// trailing raw one that happens to score the same.
//
// Cost: one pass over rows per column, with the per-term scan only on cells anyTerm
// passes. Only runs when 2+ terms are present (a single-term search never calls it), and
// only over the columns the grid is already rendering.
export function scoreColumns(
  rows: readonly unknown[][],
  cols: readonly MatchColumn[],
  terms: readonly SearchTerm[],
  exact: boolean,
): ColumnCoverage[] {
  const out: ColumnCoverage[] = [];
  const any = anyTerm(terms, exact);
  for (const c of cols) {
    const key = baseKey(c.key);
    const found = new Set<string>();
    let rowHits = 0;
    for (const r of rows) {
      const forms = cellForms(r[c.index], key);
      if (!forms.length || !any(forms)) continue;
      let hit = false;
      for (const t of terms) {
        if (cellMatches(forms, t.needle, exact)) {
          found.add(t.needle);
          hit = true;
        }
      }
      if (hit) rowHits++;
    }
    if (found.size > 0) {
      out.push({ key: c.key, label: c.label, index: c.index, hits: found.size, rows: rowHits });
    }
  }
  // Best coverage first, then the column that surfaces fewer rows (tighter = more useful
  // when two columns both carry every value), then original order.
  const order = new Map(cols.map((c, i) => [c.key, i]));
  out.sort((a, b) =>
    b.hits - a.hits
    || a.rows - b.rows
    || (order.get(a.key) ?? 0) - (order.get(b.key) ?? 0));
  return out;
}

// Does a row match ANY of the terms, within `cols`? Multi-value search is a UNION: the
// user pasted 24 references and expects the rows for all 24, which is precisely why a
// single concatenated needle found nothing.
export function rowMatchesAnyTerm(
  row: readonly unknown[],
  cols: readonly MatchColumn[],
  terms: readonly SearchTerm[],
  exact: boolean,
): boolean {
  const any = anyTerm(terms, exact);
  for (const c of cols) {
    const forms = cellForms(row[c.index], baseKey(c.key));
    if (forms.length && any(forms)) return true;
  }
  return false;
}

// Which pasted values found nothing, in the order they were pasted.
//
// This is the honest half of the feature and §6 in miniature: a value that matches no row
// must be REPORTED, never silently dropped. Pasting 24 refs and being shown 22 rows with
// no further word is how a user concludes the tool found everything when two references
// were mistyped or belong to another period.
export function unmatchedTerms(
  rows: readonly unknown[][],
  cols: readonly MatchColumn[],
  terms: readonly SearchTerm[],
  exact: boolean,
): SearchTerm[] {
  const found = new Set<string>();
  const any = anyTerm(terms, exact);
  for (const r of rows) {
    for (const c of cols) {
      const forms = cellForms(r[c.index], baseKey(c.key));
      if (!forms.length || !any(forms)) continue;
      for (const t of terms) {
        if (!found.has(t.needle) && cellMatches(forms, t.needle, exact)) found.add(t.needle);
      }
    }
    if (found.size === terms.length) break;  // everything accounted for
  }
  return terms.filter((t) => !found.has(t.needle));
}

// A searched value that sits on an ALREADY-MATCHED row.
//
// This is the other half of the search workflow. A matched row is not in the unmatched
// grid and can never be manually re-paired (the engine only pairs UNMATCHED rows), so a
// wrong auto-match is only fixable once the user can see it and release it. That holds
// whether or not the value ALSO appears on unmatched rows (finance 2026-09-28: a value
// found in the unmatched grid hid its matched rows entirely).
export interface MatchedHit {
  terms: SearchTerm[];  // every searched value on this row, in search order
  sig: string;          // the row's content signature — what the release API takes, and
                        // what sigLabel resolves back to a readable row summary
  status: string;       // "Matched" / "Figure/Balance Mismatch" — shown so the user
                        // knows whether they are breaking a tie or a mismatch
}

// Every matched row holding any of `terms`, in frame order. `rows` is the FULL frame (not
// the unmatched slice), `statusIdx`/`sigIdx` locate its match_status and _sig columns.
// ONE hit per signature: release is keyed on the signature, so rows sharing one cannot be
// told apart or released separately, and two lines would promise a choice that is not
// there. `candidate` is an optional cheap pre-test per (row position, needle); it must
// accept every pair the cell test below would, since a rejected pair is never checked.
export function findInMatched(
  rows: readonly unknown[][],
  cols: readonly MatchColumn[],
  terms: readonly SearchTerm[],
  exact: boolean,
  statusIdx: number,
  sigIdx: number,
  candidate?: (pos: number, needle: string) => boolean,
  // An OPEN row the app nonetheless paired (a Cleared in Subsequent Month pairing): its
  // status label to list it under, or null. Such a row is in the grid, yet only this
  // panel can undo its pairing.
  admit?: (pos: number) => string | null,
): MatchedHit[] {
  if (statusIdx < 0 || sigIdx < 0 || !terms.length) return [];
  const bySig = new Map<string, MatchedHit>();
  const any = anyTerm(terms, exact);
  for (let p = 0; p < rows.length; p++) {
    const r = rows[p];
    let status = String(r[statusIdx] ?? "");
    // Only rows the engine considers matched — an unmatched row is already in the grid.
    if (!status || status.toLowerCase().includes("not ")) {
      const label = admit ? admit(p) : null;
      if (!label) continue;
      status = label;
    }
    const forms = cols.map((c) => cellForms(r[c.index], baseKey(c.key)));
    if (!forms.some((f) => f.length && any(f))) continue;
    for (const t of terms) {
      if (candidate && !candidate(p, t.needle)) continue;
      let hit = false;
      for (const f of forms) {
        if (f.length && cellMatches(f, t.needle, exact)) { hit = true; break; }
      }
      if (!hit) continue;
      const sig = String(r[sigIdx] ?? "");
      let h = bySig.get(sig);
      if (!h) { h = { terms: [], sig, status }; bySig.set(sig, h); }
      if (!h.terms.includes(t)) h.terms.push(t);
    }
  }
  return [...bySig.values()];
}

// Group matched-hit signatures into one entry per MATCH, for "Release all". Two hits are
// the same match when their leg sets (the hit plus `counterparts(hit)`) share any
// signature; a hit that links two groups merges them into the earliest. Each entry keeps
// only the HIT signatures, in first-seen order — the counterparts decide grouping, never
// what is sent. Union-find over a leg index, so a broad search listing thousands of
// matched rows stays linear (a scan of every group per hit was quadratic).
export function groupHitsByMatch(
  hitSigs: readonly string[],
  counterparts: (sig: string) => readonly string[],
): string[][] {
  interface Group { seq: number; sigs: string[]; seen: Set<string>; parent: Group | null }
  const root = (g: Group): Group => {
    while (g.parent) { if (g.parent.parent) g.parent = g.parent.parent; g = g.parent; }
    return g;
  };
  const groups: Group[] = [];
  const owner = new Map<string, Group>();
  for (const sig of hitSigs) {
    const legs = [sig, ...counterparts(sig)];
    const joined: Group[] = [];
    for (const s of legs) {
      const o = owner.get(s);
      if (!o) continue;
      const g = root(o);
      if (!joined.includes(g)) joined.push(g);
    }
    joined.sort((a, b) => a.seq - b.seq);
    let into = joined[0];
    if (!into) {
      into = { seq: groups.length, sigs: [], seen: new Set(), parent: null };
      groups.push(into);
    }
    for (const g of joined.slice(1)) {
      g.parent = into;
      for (const s of g.sigs) if (!into.seen.has(s)) { into.seen.add(s); into.sigs.push(s); }
    }
    for (const s of legs) if (!owner.has(s)) owner.set(s, into);
    if (!into.seen.has(sig)) { into.seen.add(sig); into.sigs.push(sig); }
  }
  return groups.filter((g) => !g.parent).map((g) => g.sigs);
}

// Per-frame memo for the coverage scan. Scoring walks every row of every rendered column,
// which on a 69k-row Dubai frame is the one genuinely expensive part of this feature, and
// it would otherwise re-run on every keystroke and every re-render. Keyed on the frame
// object (replaced wholesale on re-finalize) plus the exact terms/columns/mode, so a
// changed paste recomputes and an unchanged one does not.
const SCORE_CACHE = new WeakMap<object, Map<string, ColumnCoverage[]>>();

export function scoreColumnsCached(
  cacheKey: object,
  rows: readonly unknown[][],
  cols: readonly MatchColumn[],
  terms: readonly SearchTerm[],
  exact: boolean,
): ColumnCoverage[] {
  let m = SCORE_CACHE.get(cacheKey);
  if (!m) { m = new Map(); SCORE_CACHE.set(cacheKey, m); }
  const k = (exact ? "1|" : "0|") + cols.map((c) => c.key).join("\u0001")
    + "\u0002" + terms.map((t) => t.needle).join("\u0001");
  const hit = m.get(k);
  if (hit) return hit;
  const scored = scoreColumns(rows, cols, terms, exact);
  // Bound the memo: a user typing into the box generates one entry per keystroke, and
  // only the most recent few are ever read again.
  if (m.size > 8) m.clear();
  m.set(k, scored);
  return scored;
}
